import { useState } from "react";
import { Globe, Check } from "lucide-react";
import type { Language } from "../data";

interface LanguagePickerProps {
  onPick: (lang: Language) => void;
}

const STORAGE_KEY = "app_lang_v1";

export function getStoredLang(): Language | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return v === "ar" || v === "en" ? v : null;
  } catch {
    return null;
  }
}

export function storeLang(lang: Language) {
  try {
    localStorage.setItem(STORAGE_KEY, lang);
  } catch {}
}

export function LanguagePicker({ onPick }: LanguagePickerProps) {
  const [selected, setSelected] = useState<Language | null>(null);

  const handleConfirm = () => {
    if (!selected) return;
    storeLang(selected);
    onPick(selected);
  };

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md">
      <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl w-full max-w-sm overflow-hidden animate-in fade-in zoom-in-95">
        <div className="bg-gradient-to-br from-[#0A2240] to-[#0D1B2E] px-6 py-7 flex flex-col items-center gap-3">
          <div className="w-14 h-14 rounded-2xl bg-blue-500/20 flex items-center justify-center">
            <Globe className="w-7 h-7 text-blue-400" />
          </div>
          <h2 className="text-lg font-bold text-white text-center">
            اختر اللغة / Choose Language
          </h2>
        </div>

        <div className="px-5 py-6 space-y-3">
          <button
            onClick={() => setSelected("ar")}
            className={`w-full flex items-center justify-between px-4 py-4 rounded-2xl border-2 transition-all ${
              selected === "ar"
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-400/40"
                : "border-stone-200 dark:border-slate-700 hover:border-blue-300"
            }`}
          >
            <span className="flex items-center gap-3">
              <span className="text-2xl">🇸🇦</span>
              <span className="font-bold text-stone-800 dark:text-slate-100 text-base">العربية</span>
            </span>
            {selected === "ar" && <Check className="w-5 h-5 text-blue-600" />}
          </button>

          <button
            onClick={() => setSelected("en")}
            className={`w-full flex items-center justify-between px-4 py-4 rounded-2xl border-2 transition-all ${
              selected === "en"
                ? "border-blue-500 bg-blue-50 dark:bg-blue-900/20 ring-2 ring-blue-400/40"
                : "border-stone-200 dark:border-slate-700 hover:border-blue-300"
            }`}
          >
            <span className="flex items-center gap-3">
              <span className="text-2xl">🇬🇧</span>
              <span className="font-bold text-stone-800 dark:text-slate-100 text-base">English</span>
            </span>
            {selected === "en" && <Check className="w-5 h-5 text-blue-600" />}
          </button>
        </div>

        <div className="px-5 pb-5 pt-1 shrink-0">
          <button
            onClick={handleConfirm}
            disabled={!selected}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-blue-600 to-sky-500 hover:from-blue-700 hover:to-sky-600 text-white font-bold text-sm transition-all shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Check className="w-4 h-4" />
            {selected === "en" ? "Confirm" : "تأكيد"}
          </button>
        </div>
      </div>
    </div>
  );
}
